import NumberFlow from "@number-flow/react";
import { use } from "react";
import { flushSync } from "react-dom";
import {
  LuChartColumn as LuBarChart2,
  LuLayers,
  LuMessagesSquare,
} from "react-icons/lu";
import { startViewTransition } from "use-start-view-transition";

import { DIALOG, setDialog } from "@/components/app/dialogs/dialog-provider";
import { EditorContextProvider } from "@/components/app/editor-v3/editor-context";
import {
  focusInlineReplyComposer,
  focusIOSKeyboardBridge,
  isIOSDocument,
} from "@/components/app/editor-v3/editor-dom-focus";
import { CommentEditor } from "@/components/app/editor-v3/editors/comment-editor";
import { toPostRequestParams } from "@/components/app/editor-v3/transforms/to-post-request-params";
import { AddToCollectionDropdown } from "@/components/shared/add-to-collection-dropdown";
import { TriggerLoggedInModalWindow } from "@/components/shared/is-logged-in";
import { RepostDropdown } from "@/components/shared/repost-dropdown";
import { StopPropagationWrapper } from "@/components/shared/stop-propagation-wrapper";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { useToast } from "@/components/ui/use-toast";
import { usePostCacheContext } from "@/contexts/post-cache-context";
import { usePostContext } from "@/contexts/post-context";
import { UserContext } from "@/contexts/user-context";
import { useSubmitPost } from "@/hooks/use-submit-post";
import { useGetCurrentUserPostStats } from "@/hooks-api/use-get-current-user-post-stats";
import { cn } from "@/utils/lib";
import { ActivityModalType } from "@/utils/post-utils";

import { PostReactionSlider } from "../post-reaction-slider";
import { PostDiamonds } from "./post-diamonds";
import { PostEmojiActivity } from "./post-emoji-activity";

type PostCompactActivityProps = {
  postHash: string;
  creatorPublicKey: string;
  wrapperClassName?: string;
  hideEmojiActivity?: boolean;
};

const NumberStat = ({ count }: { count: number | string }) => (
  <NumberFlow
    value={Number(count)}
    format={{
      notation: "compact",
    }}
  />
);

export const PostCompactActivity = ({
  postHash,
  creatorPublicKey,
  hideEmojiActivity = false,
}: PostCompactActivityProps) => {
  const {
    post: contextPost,
    updatePost,
    username,
    postCachePrefix,
  } = usePostContext();
  const post =
    contextPost?.repostedPost?.postHash === postHash
      ? contextPost.repostedPost
      : contextPost;
  const { replyCount, repostCount, viewCount, quoteCount } =
    useGetCurrentUserPostStats(post!);
  const { forceUpdate, showCommentEditor, postCacheSignal, postCacheKey } =
    usePostCacheContext(contextPost.postHash!, postCachePrefix);
  const { user } = use(UserContext);
  const submitPost = useSubmitPost();
  const { toast } = useToast();
  const toggleReplyEditor = () => {
    postCacheSignal.value = {
      ...postCacheSignal.value,
      showCommentEditor: !showCommentEditor,
    };
    forceUpdate();
  };

  return (
    <div className="flex w-full flex-col gap-2">
      {!hideEmojiActivity && (
        <div>
          <PostEmojiActivity />
        </div>
      )}

      <div className="flex justify-between pt-2">
        <TriggerLoggedInModalWindow
          className="
            flex w-full items-center justify-start gap-4 text-lg text-muted
            sm:justify-center sm:gap-6
          "
        >
          <StopPropagationWrapper
            className="
              flex items-center justify-start gap-4
              sm:justify-center sm:gap-6
            "
          >
            <div>
              <div
                role="button"
                className={cn(
                  `
                    flex items-center gap-2 text-muted
                    hover:text-primary
                  `,
                  {
                    "text-primary": showCommentEditor,
                  },
                )}
                onClick={() => {
                  if (!showCommentEditor && isIOSDocument()) {
                    focusIOSKeyboardBridge();
                    flushSync(toggleReplyEditor);
                    focusInlineReplyComposer(postCacheKey, {
                      iosActivation: true,
                    });
                    window.setTimeout(() => {
                      focusInlineReplyComposer(postCacheKey, {
                        iosActivation: true,
                      });
                    }, 0);
                    return;
                  }

                  startViewTransition(toggleReplyEditor);
                }}
              >
                <LuMessagesSquare />
                <div
                  className={cn("text-xs", {
                    hidden: replyCount! === 0,
                  })}
                >
                  <NumberStat count={replyCount!} />
                </div>
              </div>
            </div>

            <div>
              <PostReactionSlider {...{ postHash }} />
            </div>

            <div>
              <RepostDropdown updatePost={updatePost} post={post}>
                <span
                  className={cn("text-xs", {
                    hidden: repostCount! + quoteCount! === 0,
                  })}
                >
                  <NumberStat count={repostCount! + quoteCount!} />
                </span>
              </RepostDropdown>
            </div>

            <div>
              <PostDiamonds {...{ postHash, creatorPublicKey }} />
            </div>

            <div>
              <AddToCollectionDropdown
                types={["bookmark"]}
                createText="New Bookmark Collection"
                itemData={{
                  type: "post",
                  postHash,
                }}
              >
                <LuLayers
                  className="
                    cursor-pointer text-lg text-muted
                    hover:text-primary
                  "
                />
              </AddToCollectionDropdown>
            </div>
          </StopPropagationWrapper>

          <div className="ml-auto">
            <div
              className="
                flex items-end gap-2 text-right text-xs text-muted
                hover:text-primary
              "
              role="button"
              onClick={() => {
                if (!post?.postHash) {
                  return;
                }

                setDialog(DIALOG.PostActivity, {
                  open: true,
                  params: {
                    activity: ActivityModalType.view,
                    postHash: post.postHash,
                  },
                });
              }}
            >
              <LuBarChart2 className="text-lg" />
              <NumberStat count={viewCount!} />
            </div>
          </div>
        </TriggerLoggedInModalWindow>
      </div>
      {showCommentEditor && (
        <StopPropagationWrapper
          data-inline-reply-composer={postCacheKey}
          className="mt-2 border border-border p-2"
        >
          <EditorContextProvider
            autoFocus
            enableFileDrop
            onSubmit={async (editorContext) => {
              const { setEditorContext, exposed } = editorContext;
              const setModalOpen = exposed?.setModalOpen;

              setEditorContext({
                submitting: true,
              });

              const requestData = await toPostRequestParams({
                editorContext,
                user: user!,
                initialData: {
                  ParentStakeID: postHash,
                },
              });
              try {
                const updatedPost = await submitPost(requestData, {
                  toleratePostHydrationFailure: true,
                });

                if (updatedPost?.postHash) {
                  updatePost(updatedPost.postHash, updatedPost);
                }

                toast({
                  title: "Comment submitted",
                  description: "Your comment has been submitted successfully.",
                  variant: "success",
                });

                if (typeof setModalOpen === "function") {
                  setModalOpen(false);
                }
              } catch (error) {
                toast({
                  title: "Error submitting comment",
                  description: `An error happened when submitting your comment`,
                  details: createToastErrorDetails(error),
                  variant: "destructive",
                });
              } finally {
                setEditorContext({
                  submitting: false,
                });
              }
            }}
            onChange={() => {
              forceUpdate();
            }}
          >
            <CommentEditor to={username} durationClassName="duration-[0s]" />
          </EditorContextProvider>
        </StopPropagationWrapper>
      )}
    </div>
  );
};
